/* TechnicianDashboard — iter97.88 (Phase A3)
 *
 * Landing page for /coe/technician. Shows the technician their day at
 * a glance — counts (today / upcoming / completed) + today's queue with
 * one-tap deep-link into the visit detail page where they capture
 * scans + photos.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Calendar, ClockClockwise, CheckCircle, Camera, MapPin, User, Stethoscope, Phone } from "@phosphor-icons/react";

function todayISO() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function relativeDateLabel(dateStr) {
    if (!dateStr) return "";
    try {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const d = new Date(dateStr + "T00:00:00");
        const diff = Math.round((d - today) / (24 * 60 * 60 * 1000));
        if (diff === 0) return "Today";
        if (diff === 1) return "Tomorrow";
        if (diff === -1) return "Yesterday";
        if (diff > 1) return `in ${diff} days`;
        return `${Math.abs(diff)} days ago`;
    } catch { return ""; }
}

export default function TechnicianDashboard() {
    const [visits, setVisits] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/coe/technician/visits")
            .then((r) => setVisits(r.data || []))
            .catch(() => setVisits([]))
            .finally(() => setLoading(false));
    }, []);

    const t = todayISO();
    const counts = useMemo(() => {
        const today = visits.filter((v) => v.date === t && v.status === "assigned").length;
        const upcoming = visits.filter((v) => v.date > t && v.status === "assigned").length;
        const completed = visits.filter((v) => v.status === "completed").length;
        return { today, upcoming, completed };
    }, [visits, t]);

    const todayQueue = useMemo(() => (
        visits.filter((v) => v.date === t && v.status === "assigned")
            .sort((a, b) => (a.time || "").localeCompare(b.time || ""))
    ), [visits, t]);

    return (
        <div data-testid="technician-dashboard">
            <div className="mb-6">
                <h1 className="font-display text-3xl font-bold">Your scan visits</h1>
                <p className="text-muted-foreground text-sm mt-1">
                    Today's queue + every visit admin has routed to you.
                </p>
            </div>

            {/* Counts row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                <StatCard
                    icon={Calendar}
                    label="Today"
                    value={counts.today}
                    tone="primary"
                    sub="Visits booked for today"
                    testid="tech-stat-today"
                />
                <StatCard
                    icon={ClockClockwise}
                    label="Upcoming"
                    value={counts.upcoming}
                    tone="amber"
                    sub="Future bookings"
                    testid="tech-stat-upcoming"
                />
                <StatCard
                    icon={CheckCircle}
                    label="Completed"
                    value={counts.completed}
                    tone="emerald"
                    sub="Visits you wrapped up"
                    testid="tech-stat-completed"
                />
            </div>

            {/* Today's queue */}
            <div className="bg-card border border-border rounded-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                    <div>
                        <h2 className="font-semibold text-sm">Today's queue</h2>
                        <p className="text-xs text-muted-foreground">{todayQueue.length} visit{todayQueue.length === 1 ? "" : "s"} scheduled for {t}</p>
                    </div>
                    <Link to="/coe/technician/visits" className="text-xs font-semibold text-primary hover:underline" data-testid="tech-all-visits-link">
                        See all visits →
                    </Link>
                </div>

                {loading && <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>}

                {!loading && todayQueue.length === 0 && (
                    <div className="p-10 text-center text-muted-foreground" data-testid="tech-today-empty">
                        <Camera size={32} className="mx-auto mb-2 opacity-50" />
                        Nothing on the schedule today — go grab a coffee ☕
                    </div>
                )}

                {!loading && todayQueue.length > 0 && (
                    <div className="divide-y divide-border">
                        {todayQueue.map((v) => (
                            <Link
                                key={v.id}
                                to={`/coe/technician/visits/${v.id}`}
                                className="flex items-center gap-4 px-5 py-3.5 hover:bg-secondary/40 transition-colors"
                                data-testid={`tech-today-row-${v.id}`}
                            >
                                <div className="w-12 h-12 rounded-xl bg-primary/10 flex flex-col items-center justify-center text-primary shrink-0 font-mono">
                                    <span className="text-[10px] font-bold uppercase tracking-wider">{relativeDateLabel(v.date)}</span>
                                    <span className="text-sm font-bold">{v.time}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-sm">{v.patient_name || "Patient"}</div>
                                    <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                                        <Stethoscope size={12} /> {v.doctor_name || "—"} · <MapPin size={11} /> {v.doctor_clinic_name || "—"}
                                    </div>
                                </div>
                                <div className="text-xs font-semibold text-primary">Open →</div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function StatCard({ icon: Icon, label, value, sub, tone = "primary", testid }) {
    const toneClass = {
        primary: "bg-primary/10 text-primary",
        amber:   "bg-amber-100 text-amber-700",
        emerald: "bg-emerald-100 text-emerald-700",
    }[tone] || "bg-primary/10 text-primary";
    return (
        <div className="bg-card border border-border rounded-2xl p-4 flex items-center gap-3" data-testid={testid}>
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${toneClass}`}>
                <Icon size={20} weight="duotone" />
            </div>
            <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
                <div className="font-display text-2xl font-bold leading-none mt-0.5">{value}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>
            </div>
        </div>
    );
}
